import { readCookies } from '@zapier/cookies';
import { useCallback, useState } from 'react';
import { refreshSubpathJWTStatus, RefreshStatus } from '../bridge/refreshJWT';
import { JWT_REFRESH_RETRY_BACKOFF } from '../constants';
import { SessionStatus } from './types';

// Conclusive: 200 = authenticated, 401/403 = signed out. Anything else (0 from a
// network/CSRF failure, or a 5xx) is inconclusive and worth retrying.
const isInconclusive = (status: RefreshStatus): boolean =>
  status !== 200 && status !== 401 && status !== 403;

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export function useJWTRefresh() {
  // In-memory, so it starts false on every load and can never be left stale.
  const [isValidating, setIsValidating] = useState(false);

  // Refresh unconditionally (the endpoint caches tokens server-side and refreshJWT.ts
  // dedupes concurrent calls). Retry an inconclusive result twice: once immediately,
  // then once after a backoff. Returns null while still inconclusive so the caller
  // keeps the previous status.
  const performRefresh =
    useCallback(async (): Promise<SessionStatus | null> => {
      const cookies = readCookies();
      if (cookies.ssohint === 'anonymous') {
        return SessionStatus.Unauthenticated;
      }

      setIsValidating(true);
      try {
        let status = await refreshSubpathJWTStatus();
        if (isInconclusive(status)) {
          status = await refreshSubpathJWTStatus();
        }
        if (isInconclusive(status)) {
          await delay(JWT_REFRESH_RETRY_BACKOFF);
          status = await refreshSubpathJWTStatus();
        }
        if (status === 200) {
          return SessionStatus.Authenticated;
        }
        if (status === 401 || status === 403) {
          return SessionStatus.Unauthenticated;
        }
        return null;
      } finally {
        setIsValidating(false);
      }
    }, []);

  return {
    performRefresh,
    isValidating,
  };
}
